import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Key as KeyIcon,
  Refresh as RefreshIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/ui';
import PrintJobsPanel from '../../components/print/PrintJobsPanel';
import { printClient } from '../../utils/printClient';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

function isOnline(agent) {
  const seen = new Date(agent?.last_seen_at).getTime();
  return Boolean(agent?.active && Number.isFinite(seen) && Date.now() - seen <= 120000);
}

function errorText(error) {
  return error?.response?.data?.message || error.message || 'Не удалось выполнить операцию';
}

const TRANSPORT_WINDOWS = 'windows_spooler';
const TRANSPORT_RAW_TSPL = 'raw_tspl';
const TRANSPORT_LABELS = {
  [TRANSPORT_WINDOWS]: 'Windows (драйвер)',
  [TRANSPORT_RAW_TSPL]: 'TSPL по сети',
};

function printerFormFrom(printer) {
  const settings = printer?.raw_settings || {};
  return {
    transport: printer?.transport || TRANSPORT_WINDOWS,
    raw_host: printer?.raw_host || '',
    raw_port: printer?.raw_port || 9100,
    direction: Number(settings.direction) === 1 ? 1 : 0,
    gap_mm: settings.gap_mm ?? 2,
  };
}

function PrinterSettingsDialog({ printer, onClose, onSaved }) {
  const [form, setForm] = useState(() => printerFormFrom(printer));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const open = Boolean(printer);
  const discoveredHost = printer?.capabilities?.host_address || '';
  const isRaw = form.transport === TRANSPORT_RAW_TSPL;

  useEffect(() => {
    setForm(printerFormFrom(printer));
    setError('');
  }, [printer]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await printClient.updatePrinter(printer.id, {
        transport: form.transport,
        raw_host: form.raw_host.trim() || null,
        raw_port: Number(form.raw_port) || 9100,
        raw_settings: {
          direction: form.direction,
          gap_mm: Number(form.gap_mm),
        },
      });
      onSaved();
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Настройки печати: {printer?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            select
            label="Способ печати"
            value={form.transport}
            onChange={(event) => update({ transport: event.target.value })}
            helperText={isRaw
              ? 'Агент отправляет TSPL напрямую на TCP-порт принтера, минуя Windows-очередь: 1–2 с до печати'
              : 'Печать через драйвер Windows и SumatraPDF (медленнее, ~7 с на этикетку)'}
          >
            {Object.entries(TRANSPORT_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </TextField>
          {isRaw && (
            <>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="IP принтера"
                  value={form.raw_host}
                  onChange={(event) => update({ raw_host: event.target.value })}
                  placeholder={discoveredHost || '192.168.0.100'}
                  helperText={discoveredHost
                    ? `Пусто — использовать адрес из порта Windows (${discoveredHost})`
                    : 'Агент не определил IP порта — укажите вручную'}
                  fullWidth
                />
                <TextField
                  label="TCP-порт"
                  type="number"
                  value={form.raw_port}
                  onChange={(event) => update({ raw_port: event.target.value })}
                  inputProps={{ min: 1, max: 65535 }}
                  sx={{ width: 160 }}
                />
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Зазор между этикетками, мм"
                  type="number"
                  value={form.gap_mm}
                  onChange={(event) => update({ gap_mm: event.target.value })}
                  inputProps={{ min: 0, max: 10, step: 0.5 }}
                  sx={{ width: 240 }}
                />
                <FormControlLabel
                  control={(
                    <Switch
                      checked={form.direction === 1}
                      onChange={(event) => update({ direction: event.target.checked ? 1 : 0 })}
                    />
                  )}
                  label="Повернуть этикетку на 180°"
                />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Отмена</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Сохранить</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PrintAgents() {
  const [agents, setAgents] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [tokenDialog, setTokenDialog] = useState({ open: false, token: '', title: '' });
  const [printerEdit, setPrinterEdit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [agentItems, printerItems, jobData] = await Promise.all([
        printClient.listAgents({ silent: true }),
        printClient.listPrinters({ silent: true }),
        printClient.listJobs({ limit: 100 }, { silent: true }),
      ]);
      setAgents(agentItems);
      setPrinters(printerItems);
      setJobs(jobData?.items || []);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createAgent = async () => {
    try {
      const result = await printClient.createAgent(name);
      setCreateOpen(false);
      setName('');
      setTokenDialog({
        open: true,
        token: result.token,
        title: `Токен агента «${result.agent.name}»`,
      });
      await load();
    } catch (requestError) {
      setError(errorText(requestError));
    }
  };

  const toggleAgent = async (agent) => {
    try {
      await printClient.updateAgent(agent.id, { active: !agent.active });
      await load();
    } catch (requestError) {
      setError(errorText(requestError));
    }
  };

  const rotateToken = async (agent) => {
    try {
      const result = await printClient.rotateAgentToken(agent.id);
      setTokenDialog({
        open: true,
        token: result.token,
        title: `Новый токен агента «${agent.name}»`,
      });
      await load();
    } catch (requestError) {
      setError(errorText(requestError));
    }
  };

  return (
    <>
      <PageHeader
        title="Print-agents"
        subtitle="Windows-агенты, доступные принтеры и очередь сетевой печати"
        breadcrumbs={[{ label: 'Главная', path: '/' }, { label: 'Настройки' }, { label: 'Print-agents' }]}
        actions={(
          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Обновить</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              Добавить агента
            </Button>
          </Stack>
        )}
      />

      <Stack spacing={2}>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        <Alert severity="info">
          Токен показывается только при создании или ротации. Сохраните его в конфигурации Windows print-agent.
        </Alert>

        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Typography variant="titleMedium" sx={{ p: 2 }}>Агенты</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Агент</TableCell>
                  <TableCell>Состояние</TableCell>
                  <TableCell>Версия / IP</TableCell>
                  <TableCell>Последний heartbeat</TableCell>
                  <TableCell align="right">Управление</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agents.map((agent) => (
                  <TableRow key={agent.id} hover>
                    <TableCell>
                      <Typography variant="bodyMedium" fontWeight={700}>{agent.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{agent.hostname || 'Имя компьютера не получено'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={isOnline(agent) ? 'success' : (agent.active ? 'warning' : 'default')}
                        variant="outlined"
                        label={isOnline(agent) ? 'Online' : (agent.active ? 'Offline' : 'Выключен')}
                      />
                    </TableCell>
                    <TableCell>{agent.agent_version || '—'} / {agent.last_ip || '—'}</TableCell>
                    <TableCell>{formatDate(agent.last_seen_at)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ротировать токен">
                        <IconButton onClick={() => rotateToken(agent)}><KeyIcon /></IconButton>
                      </Tooltip>
                      <Switch
                        size="small"
                        checked={Boolean(agent.active)}
                        onChange={() => toggleAgent(agent)}
                        inputProps={{ 'aria-label': `Активность агента ${agent.name}` }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {!agents.length && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>Агенты не зарегистрированы</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Typography variant="titleMedium" sx={{ p: 2 }}>Доступные принтеры</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Принтер</TableCell>
                  <TableCell>Агент</TableCell>
                  <TableCell>Драйвер / порт</TableCell>
                  <TableCell>Способ печати</TableCell>
                  <TableCell>Расположение</TableCell>
                  <TableCell>Последний heartbeat</TableCell>
                  <TableCell align="right">Настройки</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {printers.map((printer) => {
                  const raw = printer.transport === TRANSPORT_RAW_TSPL;
                  return (
                    <TableRow key={printer.printer_uid} hover>
                      <TableCell>
                        <Typography variant="bodyMedium" fontWeight={700}>{printer.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{printer.printer_uid}</Typography>
                      </TableCell>
                      <TableCell>{printer.agent?.name || printer.agent?.hostname || '—'}</TableCell>
                      <TableCell>{printer.driver_name || '—'} / {printer.port_name || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={raw ? 'success' : 'default'}
                          label={TRANSPORT_LABELS[printer.transport] || printer.transport || TRANSPORT_LABELS[TRANSPORT_WINDOWS]}
                        />
                        {raw && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {printer.raw_host_effective || 'IP не задан'}:{printer.raw_port || 9100}
                            {printer.raw_settings?.direction === 1 ? ' · 180°' : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{printer.location || '—'}</TableCell>
                      <TableCell>{formatDate(printer.last_seen_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Способ печати и параметры TSPL">
                          <IconButton size="small" onClick={() => setPrinterEdit(printer)}>
                            <TuneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!printers.length && (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}>Online-принтеров нет</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <PrintJobsPanel
          jobs={jobs}
          loading={loading}
          onRefresh={load}
          onReprint={() => {}}
          canReprint={false}
          title="Последние задания всех кабинетов"
        />
      </Stack>

      <PrinterSettingsDialog
        printer={printerEdit}
        onClose={() => setPrinterEdit(null)}
        onSaved={async () => {
          setPrinterEdit(null);
          await load();
        }}
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Добавить print-agent</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Название агента"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 120 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={createAgent} disabled={!name.trim()}>Создать</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={tokenDialog.open}
        onClose={() => setTokenDialog({ open: false, token: '', title: '' })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{tokenDialog.title}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            После закрытия токен больше не будет показан.
          </Alert>
          <TextField
            fullWidth
            multiline
            value={tokenDialog.token}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<CopyIcon />}
            onClick={() => navigator.clipboard?.writeText(tokenDialog.token)}
          >
            Скопировать
          </Button>
          <Button
            variant="contained"
            onClick={() => setTokenDialog({ open: false, token: '', title: '' })}
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
